import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FeatureDetectionService } from './feature-detection.service';
import { ScriptLoadService } from './script-load.service';
import { SettingsService } from './settings.service';
import { TrackingService } from './tracking.service';

@Injectable({
    providedIn: 'root',
})
export class IndicataService implements OnDestroy {
    private readonly indicataScriptUrl = 'https://lg.indicata.com/widget/v1/widget.js';

    constructor(
        private readonly scriptLoadService: ScriptLoadService,
        private readonly settingsService: SettingsService,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly trackingService: TrackingService
    ) {}

    public ngOnDestroy(): void {
        if (this.featureDetectionService.isBrowser() && window._lgwidget?.dispose) {
            window._lgwidget.dispose();
        }
    }

    public async init() {
        if (!this.featureDetectionService.isBrowser()) {
            // Not during SSR.
            return;
        }

        if (!this.trackingService.functionalConsentGiven()) {
            // No consent, no dice.
            return;
        }

        if (window._lgwidget) {
            // Widget already loaded. Just open.
            window._lgwidget.dispose();
            window._lgwidget.embed();
            this.trackingService.trackIndicata();
            return;
        }

        if (this.scriptLoadService.isScriptInserted(this.indicataScriptUrl)) {
            // Script already added - just wait.
            return;
        }

        const { indicataDealerId } = await firstValueFrom(this.settingsService.settings$);
        if (!indicataDealerId) {
            // Only add script if ID exists.
            return;
        }

        // Add widget settings to window.
        window._lgconf = {
            dealerId: indicataDealerId,
            inline: true,
        };

        // Load Indicata widget script (opens widget after load automatically).
        this.scriptLoadService.loadScript(this.indicataScriptUrl, {
            async: true,
            callback: () => {
                this.trackingService.trackIndicata();
            },
        });
    }
}

// Extend the global Window interface
declare global {
    interface Window {
        _lgconf?: IndicataConfig;
        _lgwidget?: IndicataInstance;
    }
}

export interface IndicataInstance {
    /**
     * The configuration object used to initialize the widget.
     */
    conf: IndicataConfig;

    /**
     * A unique DOM element ID for this widget instance (e.g., "lg-widget-container-397").
     */
    id: string;

    /**
     * The final calculated position applied to the widget, based on config or defaults.
     */
    position: IndicataPosition;

    /**
     * Injects the widget into the DOM and renders the collapsed/expanded UI.
     */
    embed(): void;

    /**
     * Cleans up and removes the widget DOM container.
     */
    dispose(): void;
}

export interface IndicataConfig {
    /**
     * Your unique dealer ID (required)
     */
    dealerId: string;

    /**
     * If true, the widget does not auto-display and is triggered inline (e.g., by a link/button)
     */
    inline?: boolean;

    /**
     * Positioning of the widget using CSS lengths
     * NOTE: Not needed for "inline".
     */
    position?: IndicataPosition;

    /**
     * Internationalization / translation options
     * NOTE: Not needed for "inline".
     */
    i18n?: IndicataI18n;

    /**
     * Optional source metadata (used by some clients)
     */
    source?: string;
}

export interface IndicataPosition {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
}

export interface IndicataI18n {
    widgetTitle?: string;
    buttonText?: string;
}
